import { useEffect, useState } from "react";
import { T } from "@v0y4ge/jrni-ui";
import { listEventTemplates } from "@v0y4ge/api-client";
import { logUpsert } from "../../lib/audit";
import { can, onTenantChange, onTenantSwitching } from "../../shell/access";
import type { EventTemplate as EventTemplateDto } from "@v0y4ge/api-contracts";
import type { EventTemplate } from "./templates-data";

function tmplFromDto(d: EventTemplateDto): EventTemplate {
  return {
    id: d.id,
    typeId: d.typeId,
    name: d.name,
    type: d.type,
    usage: d.usage ?? d.usageCount ?? 0,
    lastEdit: d.lastEdit ?? d.lastEditAt ?? "",
    hue: d.hue || d.color || T.p1,
    popular: !!d.popular,
    desc: d.desc ?? d.description ?? "",
    tags: Array.isArray(d.tags) ? d.tags : [],
    defaults: {
      capacity: d.defaults?.capacity ?? 0,
      tickets: d.defaults?.tickets ?? 0,
      comms: d.defaults?.comms ?? 0,
    },
    status: d.status === "draft" ? "draft" : "published",
  };
}

/* ── Shared template store ───────────────────────────────────────────────
 * Module-level so a template created/renamed/duplicated in the UI persists
 * across screens within a session (interim client persistence). The seed is
 * the first paint; a successful fetch merges in real templates while keeping
 * any locally-authored ones. Swap the upserts for real create/update ops at
 * wiring time — see the changelog's data-layer notes. */
let _templates: EventTemplate[] = [];
let _loadStarted = false;
let _loading = true;
let _error: string | null = null;
const _localIds = new Set<string>();
const _subs = new Set<() => void>();
const _emit = () => _subs.forEach((f) => { try { f(); } catch { /* noop */ } });
// Tenant-switch handling (docket 2026-07-27, item 1): this store had NONE —
// tenant A's templates (incl. any locally-authored ones) stayed resident
// under tenant B's chrome after a switch. Purge immediately (`_loadStarted =
// true` blocks an incidental _load() call during the pending window); reload
// only once the new tenant is confirmed.
onTenantSwitching(() => { _templates = []; _loadStarted = true; _loading = true; _error = null; _localIds.clear(); _emit(); });
onTenantChange(() => { _loadStarted = false; _loading = true; if (can("events.read")) void _load(); else _loading = false; }); // admin/events/templates — gated (docket 2026-09-17)

async function _load(): Promise<void> {
  if (_loadStarted) return;
  _loadStarted = true;
  try {
    if (typeof listEventTemplates !== "function") { _loading = false; _emit(); return; }
    const r = await listEventTemplates({});
    const arr = (r as { templates?: EventTemplateDto[] })?.templates;
    if (Array.isArray(arr) && arr.length) {
      const fetched = arr.map(tmplFromDto);
      // Keep locally-authored templates that the fetch doesn't include.
      const locals = _templates.filter((t) => _localIds.has(t.id) && !fetched.some((f) => f.id === t.id));
      _templates = [...locals, ...fetched];
    }
    _loading = false;
    _emit();
  } catch (e) {
    _error = e instanceof Error ? e.message : "Failed to load templates";
    _loading = false;
    _emit();
  }
}

/** Create or update a template in the shared store (interim client persistence). */
export function upsertTemplate(t: EventTemplate): void {
  _localIds.add(t.id);
  const i = _templates.findIndex((x) => x.id === t.id);
  logUpsert("template", i >= 0 ? (_templates[i] as unknown as Record<string, unknown>) : null, t as unknown as Record<string, unknown>, { name: t.name });
  _templates = i >= 0 ? _templates.map((x) => (x.id === t.id ? t : x)) : [t, ..._templates];
  _emit();
}

/** Remove a template from the store (post-delete reflect). */
export function removeTemplateLocal(id: string): void {
  _localIds.delete(id);
  _templates = _templates.filter((t) => t.id !== id);
  _emit();
}
/** Reflect a returned template DTO into the store (post-duplicate). */
export function reflectTemplateDto(dto: EventTemplateDto): EventTemplate {
  const t = tmplFromDto(dto);
  upsertTemplate(t);
  return t;
}

/** Generate a fresh template id. */
export function newTemplateId(): string {
  return `tpl_${Date.now().toString(36)}`;
}

export function useTemplates(): { templates: EventTemplate[]; loading: boolean; error: string | null } {
  const [, force] = useState(0);
  useEffect(() => {
    const cb = () => force((n) => n + 1);
    _subs.add(cb);
    void _load();
    return () => { _subs.delete(cb); };
  }, []);
  return { templates: _templates, loading: _loading, error: _error };
}
